import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  MenuItem,
} from "@mui/material";
import { useState, useEffect } from "react";
import { handleError } from "../../utils/handleError";
import toast from "react-hot-toast";
import {
  convertToDisplayFormat,
  convertToInputDateFormat,
} from "../../utils/formatDate";

const EditUserModal = ({ open, setOpen, userData, onSave, setLoading }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    nickName: "",
    address: {
      street: "",
      city: "",
      state: "",
      postalCode: "",
    },
    dateBirth: "",
    gender: "",
    phoneNumber: "",
    status: "inactive",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (userData) {
      setFormData({
        ...userData,
        dateBirth: userData?.dateBirth
          ? convertToInputDateFormat(userData?.dateBirth)
          : "",
        address: {
          street: userData.address?.street || "",
          city: userData.address?.city || "",
          state: userData.address?.state || "",
          postalCode: userData.address?.postalCode || "",
        },
        status: userData?.status || "active",
      });
    }
  }, [userData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (["street", "city", "state", "postalCode"].includes(name)) {
      setFormData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          [name]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim())
      newErrors.firstName = "El nombre es requerido";
    if (!formData.lastName.trim())
      newErrors.lastName = "El apellido es requerido";
    if (!formData.dateBirth)
      newErrors.dateBirth = "La fecha de nacimiento es requerida";
    if (!formData.gender) newErrors.gender = "Seleccione un género";
    if (!formData.address.street) newErrors.street = "La calle es requerida";
    if (!formData.address.city) newErrors.city = "La ciudad es requerida";
    if (!formData.address.state) newErrors.state = "El estado es requerido";
    if (!formData.address.postalCode)
      newErrors.postalCode = "El código postal es requerido";
    if (!["active", "inactive", "pending"].includes(formData.status)) {
      newErrors.status = "Estado inválido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!validateForm()) return setLoading(false);

    try {
      // función externa para guardar
      const dataToSend = {
        ...formData,
        dateBirth: convertToDisplayFormat(formData.dateBirth),
      };
      // console.log("Datos a enviar:", dataToSend);
      toast.success("Información actualizada correctamente.");
      setOpen(false);
    } catch (error) {
      const { message } = handleError(error);
      toast.error(`No se pudo actualizar. ${message}`);
    }
    setLoading(false);
  };

  const isPending = userData?.status !== "active";

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>Editar Usuario</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            {/* Campos editables */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                error={!!errors.firstName}
                helperText={errors.firstName}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Apellido"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                error={!!errors.lastName}
                helperText={errors.lastName}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Fecha de Nacimiento"
                name="dateBirth"
                type="date"
                value={formData.dateBirth}
                onChange={handleChange}
                required
                InputLabelProps={{ shrink: true }}
                error={!!errors.dateBirth}
                helperText={errors.dateBirth}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Género"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                error={!!errors.gender}
                helperText={errors.gender}
                required
              >
                <MenuItem value="Masculino">Masculino</MenuItem>
                <MenuItem value="Femenino">Femenino</MenuItem>
                <MenuItem value="Otro">Otro</MenuItem>
              </TextField>
            </Grid>

            {/* Campo STATUS */}
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Estado de la cuenta"
                name="status"
                value={formData.status}
                onChange={handleChange}
                error={!!errors.status}
                helperText={
                  isPending
                    ? "No puedes cambiar el estado si es diferente a activo."
                    : errors.status
                }
                disabled={isPending}
              >
                <MenuItem value="active">Activo</MenuItem>
                <MenuItem value="inactive">Inactivo</MenuItem>
              </TextField>
            </Grid>

            {/* Dirección */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Calle"
                name="street"
                value={formData.address.street}
                onChange={handleChange}
                error={!!errors.street}
                helperText={errors.street}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Ciudad"
                name="city"
                value={formData.address.city}
                onChange={handleChange}
                error={!!errors.city}
                helperText={errors.city}
                required
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Estado"
                name="state"
                value={formData.address.state}
                onChange={handleChange}
                error={!!errors.state}
                helperText={errors.state}
                required
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Código Postal"
                name="postalCode"
                value={formData.address.postalCode}
                onChange={handleChange}
                error={!!errors.postalCode}
                helperText={errors.postalCode}
                required
              />
            </Grid>

            {/* Campos NO editables */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Email"
                value={formData.email}
                disabled
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="NickName"
                value={formData.nickName}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Teléfono"
                value={formData.phoneNumber}
                disabled
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            variant="outlined"
            color="white"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{ backgroundColor: "#e7ef7d" }}
          >
            Guardar Cambios
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditUserModal;
